import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DataTable, Input, Select, useSelect } from '@chilecompra/react-kit/components'
import routes from '../../config/settings/routes'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetSectorProvidersThunk } from './SectorProvidersSection.actions'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { Description, GridContainer, GridItem, Info, Link, Root, Title } from './SectorProvidersSection.styles'

const SortDirectionDictionary = {
  ascending: 'asc',
  descending: 'desc'
}

const SortOrderDictionary = {
  amount: 'amount',
  dni: 'rut',
  name: 'prov',
  orders: 'oc'
}

/**
 * The SectorProvidersSection's container.
 */
const SectorProvidersSection = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    endYear,
    latestUpdate,
    modalities: mechanismOptions,
    loading,
    startYear
  } = useSelector(state => state.topNavbar)
  const { id, name } = useSelector(state => state.sectorSection)
  const { data, page, pages, records } = useSelector(state => state.sectorProvidersSection)
  const [sort, setSort] = useState({ direction: 'ascending', order: 'amount' })
  const [filteredData, setFilteredData] = useState([data])
  const [searchTerm, setSearchTerm] = useState('')
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    overwriteCallback: () => endYear
  })
  const { value: mechanismValue, onChange: handleChangeMechanism } = useSelect({ initialValue: 7 })
  const { ref: providersDataLayoutRef, onDownloadFile: handleProvidersDownloadFile } = useDataLayout({
    dependencies: [yearValue]
  })
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const handleSearch = event => {
    const searchTerm = event.target.value || ''
    const filteredData = data.filter(item => {
      return item.name.toLowerCase().includes(searchTerm.toLowerCase())
    })
    setSearchTerm(searchTerm)
    setFilteredData(filteredData)
  }
  const handleChangePage = useCallback(
    (_, updatedPage) =>
      dispatch(
        onGetSectorProvidersThunk({
          id,
          direction: SortDirectionDictionary[sort.direction],
          mechanismId: mechanismValue,
          order: SortOrderDictionary[sort.order],
          page: updatedPage,
          year: yearValue
        })
      ),
    [id, mechanismValue, page, sort.direction, sort.order, yearValue]
  )
  const handleSortColumn = useCallback(
    (updatedOrder, updatedDirection) =>
      dispatch(
        onGetSectorProvidersThunk({
          id,
          page,
          direction: SortDirectionDictionary[updatedDirection],
          mechanismId: mechanismValue,
          order: SortOrderDictionary[updatedOrder],
          year: yearValue,
          callback: () => setSort({ direction: updatedDirection, order: updatedOrder })
        })
      ),
    [id, mechanismValue, page, sort.direction, sort.order, yearValue]
  )
  const handleClickRedirect = dni => () => navigate(`${routes.provider.replace(/:dni/, dni)}`)
  useEffect(() => {
    if (!id || !mechanismValue || !yearValue) {
      return
    }
    setFilteredData(data)
    dispatch(
      onGetSectorProvidersThunk({
        id,
        page,
        direction: SortDirectionDictionary[sort.direction],
        mechanismId: mechanismValue,
        order: SortOrderDictionary[sort.order],
        year: yearValue
      })
    )
  }, [id, mechanismValue, page, sort.direction, sort.order, yearValue])

  return (
    <Root>
      <GridContainer padding="25px 0 23px">
        <GridItem xs={12}>
          <Title>Proveedores</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          <Description>
            Conoce el monto total transado con cada uno de los proveedores que han transado con algún organismo
            comprador perteneciente al sector {name} según el año seleccionado.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <DataLayout
            disableCaptureImage
            disableDownloadFile
            initialDisplayedView={DataLayoutViews.TABLE}
            noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
            ref={providersDataLayoutRef}
            updatedDate={latestUpdate}
            onDownloadFile={handleProvidersDownloadFile}
            header={
              <GridContainer alignItems="center" justifyContent="space-between">
                <GridItem md={9} xs={12}>
                  <GridContainer alignItems="center" spacing={1}>
                    <GridItem md={2} xs={12}>
                      <Select
                        label="AÑO"
                        loading={loading}
                        options={yearsOptions}
                        value={yearValue}
                        onChange={handleChangeYear}
                      />
                    </GridItem>
                    <GridItem md={4} xs={12}>
                      <Select
                        label="MECANISMO DE COMPRA"
                        loading={loading}
                        options={mechanismOptions}
                        value={mechanismValue}
                        onChange={handleChangeMechanism}
                      />
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem md={3} xs={12} align="right" margin="16px 0 0 0">
                  <Input width="100%" placeholder="Buscar..." value={searchTerm} onChange={handleSearch} />
                </GridItem>
              </GridContainer>
            }
          >
            <DataLayoutChild view={DataLayoutViews.TABLE}>
              <GridContainer padding="0 0 24px">
                <GridItem xs={12}>
                  <DataTable
                    enableResults
                    customPagination={{
                      page,
                      pages,
                      records,
                      onChangePage: handleChangePage,
                      onSortColumn: handleSortColumn
                    }}
                    headers={[
                      { label: 'Nombre proveedor', value: 'name' },
                      { label: 'RUT proveedor', value: 'dni' },
                      { label: 'N° de órdenes de compra', value: 'orders' },
                      { label: 'Monto transado', value: 'amount' },
                      { label: 'Monto transado ($USD)', value: 'amountUSD' }
                    ]}
                    // FIXME: there is no way to filter using the related service.
                    headersCellsProps={[
                      { enableSort: true, width: '25%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, initialDirection: 'ascending', initialSort: true, width: '15%' },
                      { width: '15%' }
                    ]}
                    loading={loading}
                    records={filteredData?.length > 0 ? filteredData : data}
                    recordsDecorators={[
                      ({ dni, name }) => <Link onClick={handleClickRedirect(dni)}>{name}</Link>,
                      ({ dni }) => dni,
                      ({ orders }) => orders,
                      ({ amount }) => `$${formatAmount(amount)}`,
                      ({ amountUSD }) => `$${formatAmount(amountUSD)}`
                    ]}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer margin="6px 0 0">
                <GridItem xs={12}>
                  <Info>
                    La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                    impuestos)
                  </Info>
                </GridItem>
              </GridContainer>
            </DataLayoutChild>
          </DataLayout>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default SectorProvidersSection
